import React, { useEffect, useState } from "react";
import { useAdminAuth } from "./AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { Save, Loader2 } from "lucide-react";

const PLACEHOLDERS = [
  "first_name",
  "customer_name",
  "customer_phone",
  "customer_email",
  "service_title",
  "date",
  "time_window",
  "address",
  "suburb",
  "reference",
  "preferred_date",
  "message",
  "company_name",
  "company_phone",
  "company_website",
  "photo_section",
  "photo_html",
  "photo_line",
  "notes",
  "notes_section",
];

export default function AdminTemplates() {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/templates");
      setTemplates(r.data || []);
      if ((r.data || []).length && !selected) {
        setSelected(r.data[0]);
        setDraft({ subject: r.data[0].subject || "", body: r.data[0].body || "" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = (t) => {
    setSelected(t);
    setDraft({ subject: t.subject || "", body: t.body || "" });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await authAxios.put(`/templates/${selected.key}`, draft);
      setTemplates((arr) => arr.map((t) => (t.key === selected.key ? r.data : t)));
      setSelected(r.data);
      toast({ title: "Template saved" });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-extrabold text-slate-900">Message Templates</h1>
      </div>
      {loading ? (
        <div className="text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-100 text-xs font-bold text-slate-600 uppercase tracking-wider">All templates</div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {templates.map((t) => (
                <button
                  key={t.key}
                  onClick={() => pick(t)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${selected?.key === t.key ? "bg-sky-50" : ""}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${t.type === "sms" ? "bg-sky-100 text-sky-700" : "bg-purple-100 text-purple-700"}`}>{t.type.toUpperCase()}</span>
                  </div>
                  <div className="text-sm font-semibold text-slate-800">{t.label}</div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">{t.key}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            {!selected ? (
              <div className="text-sm text-slate-500">Select a template.</div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">{selected.type} template • {selected.key}</div>
                  <h2 className="font-extrabold text-lg text-slate-900">{selected.label}</h2>
                </div>
                {selected.type === "email" && (
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Subject</Label>
                    <Input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} className="mt-1 h-10" />
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Body ({selected.type === "email" ? "HTML allowed" : "plain text"})</Label>
                  <Textarea value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} rows={14} className="mt-1 font-mono text-sm" />
                </div>
                <div className="text-xs text-slate-500">
                  <strong className="text-slate-700">Available placeholders:</strong>{" "}
                  {PLACEHOLDERS.map((p) => <code key={p} className="bg-slate-100 rounded px-1 mr-1">{"{{" + p + "}}"}</code>)}
                </div>
                <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white">
                  {saving ? <><Loader2 className="animate-spin mr-2 h-4 w-4" /> Saving…</> : <><Save className="mr-2 h-4 w-4" /> Save template</>}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
